import { useState, useRef } from "react";
import { toast } from "sonner";
import TextareaAutosize from "react-textarea-autosize";
import {
  ArrowRight,
  Sparkles,
  Target,
  Users,
  Radio,
  CheckCircle2,
} from "lucide-react";
import SiteLayout from "@/components/SiteLayout";
import Container from "@/components/editorial/Container";
import Eyebrow from "@/components/editorial/Eyebrow";
import api, { formatApiError } from "@/lib/api";
import { getAttribution } from "@/lib/attribution";
import { trackEvent } from "@/lib/analytics";

const BRANDS_MEETING =
  "https://static.prod-images.emergentagent.com/jobs/fcfb0e3d-78af-4eb0-9bc1-020bea71b151/images/f385740f410246e165c3235d8900b8528c485c0896e842fdac4b2cb9452b1e09.jpeg";
const BRANDS_SCREENING =
  "https://static.prod-images.emergentagent.com/jobs/fcfb0e3d-78af-4eb0-9bc1-020bea71b151/images/03eb38f6d60c15a1b74e4e42046cf0c945aaa45b24e7252e281b589b43b3f001.jpeg";

const TIERS = [
  {
    id: "episode",
    name: "Episode Sponsor",
    price: "Consultar",
    duration: "Por episodio",
    badge: null,
    features: [
      "Mención al inicio y final del mini documental",
      "Inserción de logo en créditos editoriales",
      "1 pieza social dedicada",
      "Acceso a evento de lanzamiento del episodio",
    ],
    highlighted: false,
    variant: "dark",
  },
  {
    id: "category",
    name: "Category Partner",
    price: "Consultar",
    duration: "6 meses",
    badge: "Popular",
    badgeTone: "gold",
    features: [
      "Asociación exclusiva a una categoría",
      "Presencia en 3 episodios relacionados",
      "Integración en 4 podcasts",
      "Activación de evento privado",
      "Campaña de PR conjunta",
      "Analytics report trimestral",
    ],
    highlighted: true,
    variant: "light",
  },
  {
    id: "founding",
    name: "Founding Partner",
    price: "Consultar",
    duration: "12 meses",
    badge: "Limitado a 3",
    badgeTone: "terracotta",
    features: [
      "Asociación a la plataforma completa",
      "Presencia en todos los episodios de la temporada",
      "Co-branding en podcast + social + eventos",
      "Screening privado + summit anual",
      "Directora como spokesperson",
      "PR tier 1 conjunto",
    ],
    highlighted: false,
    variant: "dark",
  },
  {
    id: "content",
    name: "Content / Media Partner",
    price: "A convenir",
    duration: "In-kind · 12 meses",
    badge: "Intercambio",
    badgeTone: "gold-outline",
    features: [
      "Crédito editorial en cada episodio relevante",
      "Logo en sección \"Aliados editoriales\" (About + homepage)",
      "Mención en media kit",
      "Invitación a eventos cerrados",
    ],
    highlighted: false,
    variant: "dark",
  },
];

const EMPTY = {
  company_name: "", contact_name: "", contact_role: "",
  email: "", phone: "", website: "",
  tier_interest: "custom", category_preference: "",
  budget_range: "", message: "", source: "",
  hp_field: "",
};

export default function ForBrands() {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const formRef = useRef(null);

  const upd = (k, v) => {
    setForm((prev) => ({ ...prev, [k]: v }));
    if (errors[k]) setErrors((e) => ({ ...e, [k]: null }));
  };

  const scrollToForm = () => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  const pickTier = (id) => { upd("tier_interest", id); scrollToForm(); };

  const submit = async (e) => {
    e.preventDefault();
    const eobj = {};
    if (!form.company_name.trim()) eobj.company_name = "Requerido";
    if (!form.contact_name.trim()) eobj.contact_name = "Requerido";
    if (!/^\S+@\S+\.\S+$/.test(form.email)) eobj.email = "Email válido requerido";
    if (form.message.trim().length < 20) eobj.message = "Mínimo 20 caracteres";
    setErrors(eobj);
    if (Object.keys(eobj).length > 0) return;
    setSubmitting(true);
    try {
      const payload = { ...form, budget_range: form.budget_range || undefined, attribution: getAttribution() };
      const { data } = await api.post("/submissions/sponsor", payload);
      trackEvent("form_submit", { form_type: "sponsor" });
      toast.success(data.message);
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const msg =
        err.response?.status === 429
          ? "Demasiadas solicitudes. Intenta en un minuto."
          : formatApiError(err.response?.data?.detail) || "Error al enviar.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const input = "w-full bg-transparent border-b border-cream/35 py-3 text-base text-cream placeholder:text-cream/35 focus:border-gold focus:outline-none transition-colors";
  const lab = "text-[11px] uppercase tracking-[0.22em] text-cream/60 block mb-2";
  const err = (k) => errors[k] && <p className="text-[11px] text-[#E88060] mt-1">{errors[k]}</p>;

  if (done) {
    return (
      <SiteLayout>
        <></>
        <main className="min-h-[80vh] bg-charcoal text-cream flex items-center pt-28" data-testid="sponsor-success">
          <Container>
            <div className="max-w-3xl mx-auto text-center py-20">
              <CheckCircle2 className="h-16 w-16 text-gold mx-auto mb-8" strokeWidth={1} />
              <Eyebrow tone="gold" className="mb-5 block">Solicitud recibida</Eyebrow>
              <h1 className="font-serif font-light leading-[1.05] text-[clamp(2.5rem,5vw,4.5rem)] mb-8">
                Conectamos en <span className="italic">48 horas.</span>
              </h1>
              <p className="text-lg md:text-xl text-cream/80 max-w-2xl mx-auto leading-relaxed">
                Tu solicitud llegó directamente a <strong>Mariola</strong>, directora editorial.
                Recibirás respuesta en las próximas 48 horas para coordinar la presentación.
              </p>
            </div>
          </Container>
        </main>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout transparentHeader>
      <></>

      {/* HERO */}
      <section className="relative h-[80vh] min-h-[620px] w-full overflow-hidden bg-charcoal" data-testid="brands-hero">
        <img src={BRANDS_MEETING} alt="Reunión editorial con marcas" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-charcoal/55" />
        <div className="absolute inset-0 bg-gradient-to-b from-charcoal/30 via-transparent to-charcoal/95" />
        <Container className="relative z-10 h-full flex flex-col justify-end pb-20 md:pb-28">
          <div className="max-w-5xl animate-fade-up">
            <Eyebrow tone="gold" className="mb-5">Para marcas</Eyebrow>
            <h1 className="font-serif font-light text-cream leading-[0.98] tracking-tight" style={{ fontSize: "clamp(2.75rem, 6vw, 5.75rem)" }}>
              Asocia tu marca con<br /><span className="italic text-terracotta">las historias que están<br />cambiando México.</span>
            </h1>
            <p className="text-cream/85 mt-8 text-base md:text-lg max-w-2xl leading-relaxed">
              Mujeres Motor es una plataforma documental premium con alcance multicanal.
              Llega a audiencias cultas, latinas y aspiracionales a través de narrativas que importan.
            </p>
            <div className="flex flex-wrap gap-3 md:gap-4 mt-10">
              <button type="button" onClick={scrollToForm} data-testid="brands-cta-primary" className="inline-flex items-center gap-2 rounded-full bg-terracotta text-cream px-7 py-3.5 text-sm font-medium hover:bg-[#A64826] transition-all duration-[400ms]">
                Agenda una presentación <ArrowRight className="h-4 w-4" />
              </button>
              <a href="#tiers" className="inline-flex items-center gap-2 rounded-full border border-cream/55 text-cream px-7 py-3.5 text-sm backdrop-blur-sm hover:bg-cream hover:text-charcoal transition-colors duration-[400ms]">
                Ver tiers de patrocinio
              </a>
            </div>
          </div>
        </Container>
      </section>

      {/* Social proof */}
      <section className="bg-cream py-14 md:py-16 border-b border-charcoal/10" data-testid="brands-proof">
        <Container>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12">
            {[
              { v: "+2M", l: "Impresiones orgánicas proyectadas T1" },
              { v: "+30", l: "Ciudades cubiertas" },
              { v: "6 canales", l: "De distribución" },
              { v: "Baklight", l: "Production credit editorial" },
            ].map((s) => (
              <div key={s.l}>
                <p className="font-serif font-light text-3xl md:text-5xl text-terracotta leading-none">{s.v}</p>
                <p className="mt-3 text-[11px] uppercase tracking-[0.2em] text-charcoal/60">{s.l}</p>
              </div>
            ))}
          </div>
          <div className="mt-12 pt-8 border-t border-charcoal/10">
            <p className="text-[11px] uppercase tracking-[0.28em] text-charcoal/50 mb-5">Marcas fundadoras 2026</p>
            <div className="flex flex-wrap items-center gap-5">
              {[1,2,3,4,5].map((i) => (
                <div key={i} className="h-10 w-28 rounded-sm bg-charcoal/5 border border-charcoal/10 flex items-center justify-center text-[10px] text-charcoal/40">
                  Próximamente
                </div>
              ))}
            </div>
          </div>
        </Container>
      </section>

      {/* Value prop 4 cols */}
      <section className="bg-cream py-20 md:py-28" data-testid="brands-value">
        <Container>
          <div className="max-w-3xl mb-14">
            <Eyebrow tone="terracotta" className="mb-5">Por qué Mujeres Motor</Eyebrow>
            <h2 className="font-serif font-light text-[clamp(2rem,4vw,3.5rem)] leading-[1.05]">
              No vendemos pautas.<br /><span className="italic">Vendemos historias.</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { i: Sparkles, t: "Narrativa premium", d: "Producción documental con estándar cinematográfico. No contenido branded genérico." },
              { i: Target, t: "Propósito auténtico", d: "Historias reales de mujeres que ya están transformando comunidades. Tu marca se asocia al impacto, no lo performa." },
              { i: Users, t: "Audiencia curada", d: "Mujeres profesionales, líderes culturales, emprendedoras y aliadas. Alto poder adquisitivo y afinidad con propósito." },
              { i: Radio, t: "Distribución multicanal", d: "Documental, podcast, social, eventos presenciales y PR. Una alianza, múltiples puntos de contacto." },
            ].map((it, idx) => (
              <div key={idx} className="bg-white border border-charcoal/10 p-7 md:p-8 rounded-sm">
                <it.i className="h-7 w-7 text-terracotta mb-5" strokeWidth={1.25} />
                <h3 className="font-serif text-xl text-charcoal mb-2 font-light">{it.t}</h3>
                <p className="text-sm text-charcoal/70 leading-relaxed">{it.d}</p>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* Tiers */}
      <section id="tiers" className="bg-charcoal text-cream py-20 md:py-28" data-testid="brands-tiers">
        <Container>
          <div className="text-center max-w-2xl mx-auto mb-14">
            <Eyebrow tone="gold" className="mb-5 block">Tiers de patrocinio</Eyebrow>
            <h2 className="font-serif font-light text-[clamp(2rem,4vw,3.5rem)] leading-[1.05]">
              Cuatro formas de <span className="italic">asociarte.</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 md:gap-7 items-stretch">
            {TIERS.map((t) => {
              const light = t.variant === "light";
              const badgeClass =
                t.badgeTone === "terracotta"
                  ? "bg-terracotta text-cream"
                  : t.badgeTone === "gold-outline"
                  ? "bg-transparent border border-gold text-gold"
                  : "bg-gold text-charcoal";
              return (
                <div
                  key={t.id}
                  data-testid={`tier-${t.id}`}
                  className={`relative flex flex-col p-7 md:p-8 rounded-sm border ${
                    light
                      ? "bg-cream text-charcoal border-gold xl:scale-[1.03] xl:-my-2 shadow-2xl"
                      : "bg-charcoal-soft text-cream border-cream/15"
                  }`}
                >
                  {t.badge && (
                    <span
                      data-testid={`tier-badge-${t.id}`}
                      className={`absolute -top-3 left-7 text-[10px] uppercase tracking-[0.22em] px-3 py-1 rounded-full ${badgeClass}`}
                    >
                      {t.badge}
                    </span>
                  )}
                  <p className={`text-[11px] uppercase tracking-[0.22em] mb-3 ${light ? "text-terracotta" : "text-gold"}`}>
                    {t.duration}
                  </p>
                  <h3 className="font-serif text-2xl md:text-3xl font-light mb-3">{t.name}</h3>
                  <p
                    data-testid={`tier-price-${t.id}`}
                    className={`text-lg mb-6 ${light ? "text-charcoal/70" : "text-cream/70"}`}
                  >
                    {t.price}
                  </p>
                  <ul className="space-y-2.5 text-sm mb-8 flex-1">
                    {t.features.map((f) => (
                      <li key={f} className="flex gap-2">
                        <span className={light ? "text-terracotta" : "text-gold"}>·</span>
                        <span className={light ? "text-charcoal/85" : "text-cream/85"}>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    type="button"
                    onClick={() => pickTier(t.id)}
                    data-testid={`tier-cta-${t.id}`}
                    className={`inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm transition-all duration-[400ms] ${
                      light
                        ? "bg-terracotta text-cream hover:bg-[#A64826]"
                        : "border border-cream/55 text-cream hover:bg-cream hover:text-charcoal"
                    }`}
                  >
                    Postular interés <ArrowRight className="h-4 w-4" />
                  </button>
                </div>
              );
            })}
          </div>
          <p className="text-center text-xs text-cream/50 mt-12 max-w-2xl mx-auto">
            Todos los tiers incluyen reporting de performance y garantía de impacto editorial.
            Estructuras de colaboración a medida según calendario disponible.
          </p>
        </Container>
      </section>

      {/* Timeline 4 steps */}
      <section className="bg-cream py-20 md:py-28" data-testid="brands-timeline">
        <Container>
          <Eyebrow tone="terracotta" className="mb-5">Cómo funciona</Eyebrow>
          <h2 className="font-serif font-light text-[clamp(2rem,4vw,3.5rem)] leading-[1.05] mb-14 max-w-2xl">
            Cuatro pasos.<br /><span className="italic">Claros y sin fricción.</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 md:gap-6 relative">
            <div className="hidden md:block absolute top-6 left-0 right-0 h-px bg-charcoal/15" />
            {[
              ["Postulación", "Nos cuentas sobre tu marca y objetivos."],
              ["Presentación", "Agendamos reunión editorial (45 min)."],
              ["Propuesta a la medida", "Deck custom con plan, audiencias y producción."],
              ["Ejecución y reporte", "Producción → distribución → análisis de impacto."],
            ].map(([t, d], i) => (
              <div key={i} className="relative">
                <div className="relative h-12 w-12 rounded-full bg-cream border-2 border-terracotta flex items-center justify-center font-serif text-lg text-terracotta mb-5 z-10">
                  {i + 1}
                </div>
                <h3 className="font-serif text-xl text-charcoal mb-2 font-light">{t}</h3>
                <p className="text-sm text-charcoal/70 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* Manifesto */}
      <section className="bg-cream-dark py-20 md:py-28">
        <Container>
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-6 order-2 lg:order-1">
              <Eyebrow tone="terracotta" className="mb-5">Manifesto</Eyebrow>
              <blockquote className="font-serif italic font-light text-[clamp(1.75rem,3vw,2.5rem)] leading-[1.2] text-charcoal">
                “Creemos que las marcas más fuertes de esta década serán las que se asocien con historias reales, no las que las inventen.”
              </blockquote>
              <p className="mt-8 text-[11px] uppercase tracking-[0.22em] text-charcoal/60">
                — Equipo editorial, Mujeres Motor
              </p>
            </div>
            <div className="lg:col-span-6 order-1 lg:order-2">
              <div className="relative aspect-[4/3] overflow-hidden rounded-sm">
                <img src={BRANDS_SCREENING} alt="Screening privado de marcas" loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* SPONSOR FORM */}
      <section ref={formRef} className="bg-charcoal text-cream py-20 md:py-28" data-testid="brands-form-section">
        <Container>
          <div className="max-w-[880px] mx-auto">
            <Eyebrow tone="gold" className="mb-4">Agenda una presentación</Eyebrow>
            <h2 className="font-serif font-light text-[clamp(2rem,4.5vw,3.75rem)] leading-[1.05] mb-12">
              Conectemos en <span className="italic">48 horas.</span>
            </h2>

            <form onSubmit={submit} data-testid="sponsor-form" className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8" noValidate>
              <div>
                <label className={lab}>Nombre contacto *</label>
                <input className={input} value={form.contact_name} onChange={(e) => upd("contact_name", e.target.value)} data-testid="sf-contact-name" />
                {err("contact_name")}
              </div>
              <div>
                <label className={lab}>Rol</label>
                <input className={input} value={form.contact_role} onChange={(e) => upd("contact_role", e.target.value)} placeholder="Director de Marketing" />
              </div>
              <div>
                <label className={lab}>Email *</label>
                <input type="email" className={input} value={form.email} onChange={(e) => upd("email", e.target.value)} data-testid="sf-email" />
                {err("email")}
              </div>
              <div>
                <label className={lab}>Teléfono</label>
                <input className={input} value={form.phone} onChange={(e) => upd("phone", e.target.value)} />
              </div>
              <div>
                <label className={lab}>Empresa *</label>
                <input className={input} value={form.company_name} onChange={(e) => upd("company_name", e.target.value)} data-testid="sf-company" />
                {err("company_name")}
              </div>
              <div>
                <label className={lab}>Website</label>
                <input className={input} value={form.website} onChange={(e) => upd("website", e.target.value)} placeholder="https://tu-marca.com" data-testid="sf-website" />
              </div>

              <div>
                <label className={lab}>Cómo nos conociste</label>
                <select className={`${input} appearance-none`} value={form.source} onChange={(e) => upd("source", e.target.value)}>
                  <option value="" className="bg-charcoal">—</option>
                  <option value="linkedin" className="bg-charcoal">LinkedIn</option>
                  <option value="instagram" className="bg-charcoal">Instagram</option>
                  <option value="referido" className="bg-charcoal">Referido</option>
                  <option value="medios" className="bg-charcoal">Medios</option>
                  <option value="otro" className="bg-charcoal">Otro</option>
                </select>
              </div>
              <div>
                <label className={lab}>Presupuesto aproximado</label>
                <select className={`${input} appearance-none`} value={form.budget_range} onChange={(e) => upd("budget_range", e.target.value)}>
                  <option value="" className="bg-charcoal">Preferir no decir</option>
                  <option value="<500k" className="bg-charcoal">Menos de $500k MXN</option>
                  <option value="500k-1M" className="bg-charcoal">$500k – $1M MXN</option>
                  <option value="1M-3M" className="bg-charcoal">$1M – $3M MXN</option>
                  <option value="3M+" className="bg-charcoal">$3M+ MXN</option>
                </select>
              </div>

              <div className="md:col-span-2">
                <label className={lab}>Tier de interés</label>
                <div className="flex flex-wrap gap-2" data-testid="sf-tier">
                  {[
                    ["founding", "Founding"],
                    ["category", "Category"],
                    ["episode", "Episode"],
                    ["content", "Content / Media"],
                    ["custom", "A definir"],
                  ].map(([id, lbl]) => (
                    <label key={id} className={`cursor-pointer border rounded-full px-4 py-2 text-sm transition-colors ${
                      form.tier_interest === id
                        ? "bg-terracotta border-terracotta text-cream"
                        : "border-cream/35 text-cream/75 hover:border-cream"
                    }`}>
                      <input type="radio" name="tier" value={id} checked={form.tier_interest === id} onChange={() => upd("tier_interest", id)} className="sr-only" />
                      {lbl}
                    </label>
                  ))}
                </div>
              </div>

              {form.tier_interest === "category" && (
                <div className="md:col-span-2">
                  <label className={lab}>¿A qué categoría quieres asociarte?</label>
                  <input className={input} value={form.category_preference} onChange={(e) => upd("category_preference", e.target.value)} placeholder="Tecnología, Artesanía, Ciencia…" />
                </div>
              )}

              <div className="md:col-span-2">
                <div className="flex justify-between items-baseline">
                  <label className={lab}>Mensaje *</label>
                  <span className={`text-[11px] ${form.message.length >= 20 ? "text-gold" : "text-cream/40"}`}>
                    {form.message.length} / 1500
                  </span>
                </div>
                <TextareaAutosize className={input} minRows={3} maxLength={1500} value={form.message} onChange={(e) => upd("message", e.target.value)} data-testid="sf-message" placeholder="Cuéntanos sobre tu marca, objetivos y cualquier contexto relevante." />
                {err("message")}
              </div>

              {/* honeypot with distinct name */}
              <div aria-hidden="true" style={{ position: "absolute", left: -10000, width: 1, height: 1, overflow: "hidden" }}>
                <label htmlFor="hp_field">HP</label>
                <input id="hp_field" type="text" tabIndex={-1} autoComplete="off" value={form.hp_field} onChange={(e) => upd("hp_field", e.target.value)} />
              </div>

              <div className="md:col-span-2 flex flex-col sm:flex-row sm:items-center gap-4 pt-6">
                <button type="submit" disabled={submitting} data-testid="sponsor-submit" className="inline-flex items-center justify-center gap-2 rounded-full bg-terracotta text-cream px-8 py-4 text-sm font-medium hover:bg-[#A64826] transition-all duration-[400ms] disabled:opacity-60">
                  {submitting ? "Enviando…" : "Enviar solicitud"}
                  {!submitting && <ArrowRight className="h-4 w-4" />}
                </button>
                <p className="text-xs text-cream/50 max-w-sm">
                  Tu solicitud llega directamente a la directora editorial. Respuesta en 48 horas.
                </p>
              </div>
            </form>
          </div>
        </Container>
      </section>
    </SiteLayout>
  );
}
